import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Briefcase, Building2, Loader2, Send, X, ArrowRight } from "lucide-react";
import { PageHero, Eyebrow } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getCareers, applyToJob } from "@/lib/api";

const CONTRACT_STYLES = {
  CDI: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CDD: "bg-blue-50 text-blue-700 border-blue-200",
  Stage: "bg-amber-50 text-amber-700 border-amber-200",
  Alternance: "bg-purple-50 text-purple-700 border-purple-200",
  Freelance: "bg-pink-50 text-pink-700 border-pink-200",
};

export default function NousRejoindre() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openJob, setOpenJob] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getCareers()
      .then(setJobs)
      .catch(() => toast.error("Impossible de charger les offres."))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error("Renseignez votre nom, email et message.");
    }
    setSending(true);
    try {
      await applyToJob({ job_id: openJob.id, ...form });
      toast.success("Candidature envoyée. Nous vous répondrons rapidement.");
      setOpenJob(null);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Envoi impossible. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Nous rejoindre"
        titleLines={["Bâtissons ensemble", "le pont Europe · Afrique"]}
        subtitle="Rejoignez une équipe passionnée par l'impact, la rigueur et l'ancrage terrain — de Paris à Cotonou en passant par Abidjan."
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="careers-list">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Eyebrow>Offres ouvertes</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-navy">
              Nos <em className="not-italic italic text-gold-gradient">opportunités</em>
            </h2>
            <p className="mt-4 text-anthracite font-body leading-relaxed">
              Consulting, compliance, digital, intelligence stratégique — nous recrutons des talents à Paris, en Outre-mer et en Afrique de l'Ouest.
            </p>
          </Reveal>

          {loading ? (
            <div className="flex justify-center py-20" data-testid="careers-loading">
              <Loader2 size={30} className="animate-spin text-gold" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-14 text-center" data-testid="careers-empty">
              <Briefcase size={40} className="mx-auto text-navy/40" />
              <p className="mt-4 font-display text-xl text-navy">Pas d'offre ouverte pour le moment</p>
              <p className="mt-2 text-anthracite font-body">Envoyez-nous une candidature spontanée depuis la page contact — nous étudions chaque profil.</p>
            </div>
          ) : (
            <Stagger className="grid gap-5">
              {jobs.map((job) => (
                <StaggerItem key={job.id}>
                  <div
                    className="card-lumen group grid gap-5 rounded-2xl border border-navy/10 bg-white p-7 sm:grid-cols-[1fr_auto] sm:items-center hover:border-gold transition-colors"
                    data-testid={`career-card-${job.id}`}
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${CONTRACT_STYLES[job.contract] || "bg-navy/5 text-navy border-navy/10"}`}>
                          {job.contract}
                        </span>
                        {job.department && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-navy/5 px-3 py-1 text-xs font-body text-navy">
                            <Building2 size={12} /> {job.department}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-cream px-3 py-1 text-xs font-body text-anthracite">
                          <MapPin size={12} /> {job.location}
                        </span>
                      </div>
                      <h3 className="font-display text-2xl text-navy leading-tight">{job.title}</h3>
                      <p className="mt-2 text-sm text-anthracite font-body line-clamp-2">{job.description}</p>
                    </div>
                    <button
                      onClick={() => setOpenJob(job)}
                      data-testid={`career-apply-${job.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-gradient-to-r hover:from-gold hover:to-gold-light hover:text-navy"
                    >
                      Voir & postuler <ArrowRight size={15} />
                    </button>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      <AnimatePresence>
        {openJob && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-night/70 backdrop-blur-sm p-4"
            onClick={() => setOpenJob(null)}
            data-testid="career-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl"
            >
              <button
                onClick={() => setOpenJob(null)}
                data-testid="career-modal-close"
                className="absolute right-5 top-5 rounded-full p-2 text-anthracite hover:bg-navy/5"
              >
                <X size={20} />
              </button>
              <div className="border-b border-navy/10 p-8">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${CONTRACT_STYLES[openJob.contract] || "bg-navy/5 text-navy border-navy/10"}`}>{openJob.contract}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-cream px-3 py-1 text-xs text-anthracite"><MapPin size={12} /> {openJob.location}</span>
                </div>
                <h3 className="font-display text-3xl text-navy leading-tight">{openJob.title}</h3>
              </div>
              <div className="space-y-6 p-8">
                <div>
                  <h4 className="font-display text-sm uppercase tracking-[0.2em] text-navy mb-3">Description</h4>
                  <p className="text-anthracite font-body leading-relaxed whitespace-pre-line">{openJob.description}</p>
                </div>
                {openJob.responsibilities?.length > 0 && (
                  <div>
                    <h4 className="font-display text-sm uppercase tracking-[0.2em] text-navy mb-3">Responsabilités</h4>
                    <ul className="space-y-2">
                      {openJob.responsibilities.map((r, i) => (
                        <li key={i} className="text-anthracite font-body">• {r}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {openJob.requirements?.length > 0 && (
                  <div>
                    <h4 className="font-display text-sm uppercase tracking-[0.2em] text-navy mb-3">Profil recherché</h4>
                    <ul className="space-y-2">
                      {openJob.requirements.map((r, i) => (
                        <li key={i} className="text-anthracite font-body">• {r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <form onSubmit={submit} className="rounded-2xl border border-gold/30 bg-cream/60 p-6" data-testid="career-apply-form">
                  <h4 className="font-display text-lg text-navy mb-4">Postuler</h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="c-name">Nom complet *</Label>
                      <Input id="c-name" data-testid="apply-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <Label htmlFor="c-email">Email *</Label>
                      <Input id="c-email" type="email" data-testid="apply-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="c-phone">Téléphone</Label>
                      <Input id="c-phone" data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="c-msg">Message & lien CV *</Label>
                      <Textarea id="c-msg" rows={4} data-testid="apply-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Parlez-nous de vous, joignez un lien vers votre CV / LinkedIn…" className="mt-2" />
                    </div>
                  </div>
                  <button type="submit" disabled={sending} data-testid="apply-submit" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <>Envoyer ma candidature <Send size={15} /></>}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
